import {RouteComponentProps, withRouter} from "react-router-dom";
import {withStyles} from "@material-ui/core";
import {connect, ConnectedProps} from "react-redux";
import {State} from "../../state";
import {fetchProject, updateProject} from "../../state/project";
import {fetchBasicInfoChains} from "../../state/chains";
import React, {Component} from "react";
import {StateComponent} from "../../interfaces/project";
import HorizontalPercentageBarChart from "../charts/HorizontalBarChart";
import PiePercentageChart from "components/charts/PiePercentageChart";
import GainsLossesTable from "components/charts/GainsLossesTable";
import InsightTable from "components/charts/InsightTable";
import VerticalPercentageBarChart from "../charts/VerticalPercentageBarChart";
import NoDataLightTheme from "../NoDataLightTheme";
import {fetchGainingBrands, fetchGraphInfo, ResetRecapState, fetchResetRecapComparison } from "state/reset-recap-report";
import {PAGEVIEW_EVENTS} from "../../state/userEvents";

type PropsFromRedux = ConnectedProps<typeof connecter> & RouteComponentProps & ResetRecapProps;

interface ResetRecapProps {
  selectedProjectId: number;
  projectId: number;
  selectedStores: number[];
}


const lineSeparatorCss: React.CSSProperties = {
  height: "1px",
  width: "100%%",
  backgroundColor: "#53598D26",
  margin: "5px auto",
};

const reportContainerCss: React.CSSProperties ={
  // height:"300px",
  height:"400px",
  overflow:"auto"
}

const getChartContentCSS = (ismobile: boolean): React.CSSProperties => ({
  display: "flex",
  width: ismobile ? "95%" : "100%",
});

const loadingContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  minHeight: "300px",
};

const spinnerStyle: React.CSSProperties =  {
  width: "48px",
  height: "48px",
  border: "4px solid #e5e7eb",
  borderTop: "4px solid #f59e0b",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const  mapWithChartInfo = (data:any) =>{
 return data.map((each:any)=>({label:each?.manufacturer,value:Math.abs(each?.cubic_share_change)}))
}

const mapAssociatedBrands = (data: any[] = []) =>
  data.map((d: any) => ({
    label: d.brand,
    value: d.cubic_share_diff
  }));

const mapAssociatedPackages = (data: any[] = []) =>
  data.map((d: any) => ({
    label: d.product_nm,
    value: d.cubic_share_diff
  }));

class ResetRecap extends Component<PropsFromRedux, StateComponent> {
  // async componentDidMount() {
  //   const { projectId} = this.props;
  //   await this.loadProjectData(projectId);
  // }

  async componentDidMount() {
    const { projectId, selectedProjectId, selectedStores } = this.props;

    // if (!selectedStores || selectedStores.length === 0) return;

    if (selectedProjectId === -1) {
      this.loadProjectData(projectId,selectedStores);
    } else {
      this.loadComparisonProjectData(projectId, selectedProjectId, selectedStores);
    }
  }

  componentDidUpdate(prevProps: PropsFromRedux) {
    const { projectId, selectedProjectId, selectedStores } = this.props;
    const prevStores = prevProps.selectedStores || [];
    const currStores = selectedStores || [];
    const storesChanged =
      prevStores.length !== currStores.length ||
      prevStores.some((id, idx) => id !== currStores[idx]);
    const projectChanged =
      prevProps.selectedProjectId !== selectedProjectId;

    if (storesChanged || projectChanged) {
      if (selectedProjectId === -1) {
        this.loadProjectData(projectId,selectedStores);
      } else {
        this.loadComparisonProjectData(projectId, selectedProjectId, selectedStores);
      }
    }
  }

  loadProjectData = async (selectedProjectId: number,stores:any) => {
    try {
      const body ={projectId:selectedProjectId,stores:stores}
      // this.props.fetchGainingBrands(selectedProjectId)
      this.props.fetchGraphInfo(body)
    } catch (error) {
      console.error("Error loading project data:", error);
    }

  };

  loadComparisonProjectData = async (baseProjectId: number, comparisonProjectId: number, storeIds: number[]) => {
    try {
      this.props.fetchResetRecapComparison(baseProjectId, comparisonProjectId, storeIds);
    } catch (error) {
      console.error("Error loading comparison data:", error);
    }
  };

  reorderChartData = (data: any[]) => {
    if (!data || data.length === 0) return data;
    const sorted = [...data].sort((a, b) => b.value - a.value);
    if (sorted.length === 1) return sorted;
    const reordered: any[] = [];
    let left = 0;
    let right = sorted.length - 1;
    let toggle = true;
    while (left <= right) {
      if (toggle) { reordered.push(sorted[left++]);  } else { reordered.push(sorted[right--]); }
      toggle = !toggle;
    }
    return reordered;
  };

  /** Transform chart data: include diff only for comparison, exclude for non-comparison */
  transformChartDataForDisplay = (data: any[], isComparison: boolean) => {
    if (!data || !Array.isArray(data)) return data;

    if (isComparison) {
      // For comparison: if diff is missing, set it to 0
      return data.map((item: any) => {
        if (!("cubic_share_diff" in item)) {
          return { ...item, cubic_share_diff: 0 };
        }
        return item;
      });
    } else {
      return data;
    }
  };

  render() {
    const { selectedProjectId, chartsInfo, loading, hasFetched } = this.props;
    const isComparison = selectedProjectId !== -1;

    // Predeclare with safe defaults (empty arrays) so the charts can render gracefully
    let cubicShareBrewer: any[] = [];
    let cubicShareByWAMP: any[] = [];
    let cubicShareByPackage: any[] = [];
    let cubicShareByBrand: any[] = [];
    let cubicShareByProduct: any[] = [];

    let associatedBrands: any = {};
    let associatedPackages: any = {};
    let packagesAdded: any[] = [];
    let packagesDeleted: any[] = [];

    if (chartsInfo?.allChartsInfo) {
      const all = chartsInfo.allChartsInfo;
      cubicShareBrewer   = all.cubicSharebyBrewer;
      cubicShareByWAMP   = this.reorderChartData(all.cubicSharebyWAMP);
      cubicShareByPackage= all.cubicSharebyPackSize;
      cubicShareByBrand  = this.transformChartDataForDisplay(all.cubicSharebyBrand, isComparison);
      cubicShareByProduct= this.transformChartDataForDisplay(all.cubicSharebyProduct, isComparison);

      const associatedBrands = all?.associatedBrands || {};
      const associatedPackages = all?.associatedPackages || {};

      packagesAdded =
  chartsInfo?.allChartsInfo?.packagesAdded?.map((item: any) => ({
    label: item.label,
    delta: item.delta,
    // metric: item.maintains
  })) || [];
      packagesDeleted =
  chartsInfo?.allChartsInfo?.packagesDeleted?.map((item: any) => ({
    label: item.label,
    delta: item.delta,
    // metric: item.maintains
  })) || [];
    }
    const ismobile = window.innerWidth <= 1024;
    const chartContentCSS = getChartContentCSS(ismobile);

    const hasAnyChartData = [
      cubicShareBrewer,
      cubicShareByWAMP,
      cubicShareByPackage,
      cubicShareByBrand,
      cubicShareByProduct,
      packagesAdded,
      packagesDeleted,
      mapAssociatedBrands(chartsInfo?.allChartsInfo?.associatedBrands?.gains || []),
      mapAssociatedBrands(chartsInfo?.allChartsInfo?.associatedBrands?.losses || []),
      mapAssociatedPackages(chartsInfo?.allChartsInfo?.associatedPackages?.gains || []),
      mapAssociatedPackages(chartsInfo?.allChartsInfo?.associatedPackages?.losses || []),
    ].some((arr) => Array.isArray(arr) && arr.length > 0);

    if (!loading && hasFetched && !hasAnyChartData && !ismobile) {
      return (
        <div className="reset-recap-container">
          {/* <div className="reset-recap-header">
            Reset Recap Report
            <div className="reset-recap-sub-header">
              Current shelf space distribution and concentration risks
            </div>
          </div> */}
          <div style={lineSeparatorCss} />
          <NoDataLightTheme />
        </div>
      );
    }

    return (
      <div className="reset-recap-container">
        {/* <div className="reset-recap-header">
          Reset Recap Report
          <div className="reset-recap-sub-header">
            Current shelf space distribution and concentration risks
          </div>
        </div> */}

        <div style={lineSeparatorCss} />
        <div className="reset-recap-chart-container" style={reportContainerCss}>
          {/* <div className="module-headers">
            <label>Baseline Status</label>
          </div> */}

          <div className="charts-row" style={chartContentCSS}>
            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              <div className="chart-headers">Cubic Share by Brewer</div>
              {/* <div className="chart-sub-header">Total shelf space by brewer</div> */}
              {loading ? (
                <div style={loadingContainerStyle}>
                  <div style={spinnerStyle}></div>
                </div>
              ) : (
              <HorizontalPercentageBarChart data={cubicShareBrewer} width={450} />
              )}
            </div>

            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              <div className="chart-headers">Cubic Share by WAMP</div>
              {/* <div className="chart-sub-header">
                Shelf space by pricing and positioning segment
              </div> */}
              {loading ? (
                <div style={loadingContainerStyle}>
                  <div style={spinnerStyle}></div>
                </div>
              ) : (
              <PiePercentageChart data={cubicShareByWAMP} width={320} />
              )}
            </div>

            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              <div className="chart-headers">Cubic Share by Pack Size</div>
              {/* <div className="chart-sub-header">Shelf space by package format</div> */}
              {loading ? (
                <div style={loadingContainerStyle}>
                  <div style={spinnerStyle}></div>
                </div>
              ) : (
              <HorizontalPercentageBarChart data={cubicShareByPackage} width={450} />
              )}
            </div>
          </div>

          <div className="charts-row" style={chartContentCSS}>
            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              <div className="chart-headers">Cubic Share by Brand</div>
              {/* <div className="chart-sub-header">Total shelf space by brand</div> */}
              {loading ? (
                <div style={loadingContainerStyle}>
                  <div style={spinnerStyle}></div>
                </div>
              ) : (
              <VerticalPercentageBarChart barColor="#D1A33C" data={cubicShareByBrand} />
              )}
            </div>

            <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
              <div className="chart-headers">Cubic Share by Product</div>
              {/* <div className="chart-sub-header">Total shelf space by product</div> */}
              {loading ? (
                <div style={loadingContainerStyle}>
                  <div style={spinnerStyle}></div>
                </div>
              ) : (
              <VerticalPercentageBarChart barColor="#D1A33C" data={cubicShareByProduct} />
              )}
            </div>
          </div>

          {selectedProjectId !== -1 && (
            <>
              <div  className="charts-row" style={chartContentCSS}>
                <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                  {/* <div className="chart-headers">Associated Brands</div>
                  <div className="chart-sub-header">
                    Brands linked to observed gains or losses in this view.
                  </div> */}

                  <div className="charts-row" style={chartContentCSS}>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <GainsLossesTable
                        data={mapAssociatedBrands(chartsInfo?.allChartsInfo?.associatedBrands?.gains)}
                        Title="Top 10 brands gaining share"
                      />
                      )}
                    </div>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <GainsLossesTable
                        data={mapAssociatedBrands(chartsInfo?.allChartsInfo?.associatedBrands?.losses)}
                        Title="Bottom 10 brands losing share"
                      />
                      )}
                    </div>
                  </div>
                </div>

                <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                  {/* <div className="chart-headers">Associated Packages</div>
                  <div className="chart-sub-header">
                    Packages linked to observed gains or losses in this view.
                  </div> */}

                  <div className="charts-row" style={chartContentCSS}>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <GainsLossesTable
                        data={mapAssociatedPackages(chartsInfo?.allChartsInfo?.associatedPackages?.gains)}
                        Title="Top 10 products gaining share"
                      />
                      )}
                    </div>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <GainsLossesTable
                        data={mapAssociatedPackages(chartsInfo?.allChartsInfo?.associatedPackages?.losses)}
                        Title="Bottom 10 products losing share"
                      />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="charts-row" style={chartContentCSS}>
                <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                  {/* <div className="chart-headers">New and removed packages</div>
                  <div className="chart-sub-header">
                    Show packages added or removed in this reset. Use this to understand whether
                    performance changes are driven by new introductions or deletions.
                  </div> */}

                  <div className="charts-row" style={chartContentCSS}>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <InsightTable
                        title="Top 10 Adds"
                        metricHeader="Distribution"
                        data={packagesAdded}
                      />
                      )}
                    </div>
                    <div className="chart-styles" style={{ flex: 1, minWidth: 0 }}>
                      {loading ? (
                        <div style={loadingContainerStyle}>
                          <div style={spinnerStyle}></div>
                        </div>
                      ) : (
                      <InsightTable
                        title="Top 10 Deletes"
                        metricHeader="Distribution"
                        data={packagesDeleted}
                      />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

const connecter = connect(
  (state: State) => ({
    chartsInfo:state.resetRecapDetails.chartsInfo,
    resetRecapDetails: state.resetRecapDetails.content,
    loading: state.resetRecapDetails.loading,
    hasFetched: state.resetRecapDetails.hasFetched,
  }),
  {fetchGraphInfo,
    // fetchGainingBrands,
    fetchResetRecapComparison }
);
export default connecter(withRouter(ResetRecap));
